/*
 * nadi-suggest.js — search suggestions while you type, in Hebrew.
 *
 * WHY THIS EXISTS. Shopify's predictive-search endpoint does not serve this
 * store. /search/suggest.json?q=… returns 417 "Unsupported buyer locale" for
 * every Hebrew query; the identical request under /en/ returns results.
 * Measured 2026-09-23 and reproduced by Nadav on his phone: typing "אולפלקס"
 * in the field produced nothing at all — no list, no "no results", nothing.
 *
 * The ordinary search engine is fine in Hebrew, including partial words
 * (q=אולפ returns the 12 Olaplex products), so the suggestions are built on
 * that instead, through templates/search.nadisuggest.liquid.
 *
 * WHAT IT BINDS TO. Every field that posts to /search: the site-wide bar,
 * the one on the search page, and Kalles' own header drawer. The drawer is not
 * optional — the site-wide bar is deliberately hidden on product pages, and
 * those are where most sessions land, so for that visitor the magnifier is the
 * only search there is. Kalles' own live search is switched off in theme
 * settings (predictive_search: false), so nothing is being fought over.
 *
 * RULES THAT KEEP IT HONEST ON A PHONE
 *   - 2 characters minimum, 220ms after typing stops. A request per keystroke
 *     is four wasted round trips for one word.
 *   - Every request aborts the one before it, so a slow answer for "אול"
 *     can never overwrite the answer for "אולפלקס".
 *   - Answers are cached per query, so backspacing is instant and free.
 *   - The panel never moves the page: it is absolutely positioned over it.
 *     The store's CLS is already poor and a list that pushes the content down
 *     while someone is typing is worse than no list.
 */
(function () {
  'use strict';

  /* The store's collections, printed once per page by nadi-brand-data.liquid.
     Matched here rather than in the suggest request: doing it in Liquid cost
     200–400ms on every keystroke, and this costs nothing. */
  var BRANDS = (function () {
    var el = document.getElementById('NadiBrands');
    if (!el) return [];
    try { return JSON.parse(el.textContent) || []; } catch (e) { return []; }
  })();

  function brandRows(q) {
    if (!BRANDS.length || q.length < 2) return '';
    var lc = q.toLowerCase(), out = '', n = 0;
    for (var i = 0; i < BRANDS.length && n < 2; i++) {
      if (String(BRANDS[i][0]).toLowerCase().indexOf(lc) === -1) continue;
      n++;
      /* A brand and a category are both shelves, but they are not the same
         promise — "יודיבה" is a maker, "מסכה לשיער" is a shelf of many. The
         list already knows which is which. */
      out += '<li role="presentation"><a class="nadi-sug__row nadi-sug__row--col" role="option"' +
        ' aria-selected="false" tabindex="-1" href="/collections/' + encodeURIComponent(BRANDS[i][1]) + '">' +
        '<span class="nadi-sug__tag">' + (BRANDS[i][2] === 1 ? 'מותג' : 'קטגוריה') + '</span>' +
        '<span class="nadi-sug__body"><span class="nadi-sug__title">' + esc(BRANDS[i][0]) + '</span></span></a></li>';
    }
    return out;
  }

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  var MIN = 2;
  var WAIT = 220;
  var VIEW = 'nadisuggest';
  var cache = new Map();
  var CACHE_MAX = 30;

  function build(form) {
    /* The panel is positioned against the form, so the form has to be a
       containing block. Ours already is; the one on the search page is
       position:static, and the first test put the list 818px down the page at
       full bleed instead of under the field. Never assume the host is
       positioned — ask, and fix it if it is not. */
    if (getComputedStyle(form).position === 'static') form.style.position = 'relative';
    var panel = document.createElement('div');
    panel.className = 'nadi-sug';
    panel.hidden = true;
    form.appendChild(panel);
    return panel;
  }

  function bind(input) {
    var form = input.form;
    if (!form || input.dataset.nadiSuggest) return;
    input.dataset.nadiSuggest = '1';

    var panel = build(form);
    var id = 'NadiSug-' + (input.id || Math.random().toString(36).slice(2));
    panel.id = id;
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-controls', id);

    var timer = null;
    var controller = null;
    var open = false;
    var rows = [];
    var cursor = -1;

    function close() {
      if (!open) return;
      open = false;
      panel.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      cursor = -1;
    }

    /* ANCHOR TO THE FIELD, NOT TO THE FORM. The panel is a child of the form so
       it inherits its stacking and dies with it, but a form can be much bigger
       than its input: Kalles' header drawer carries the search chips inside the
       same form, and the list first appeared 105px below the field and 40px
       wider than it. offsetTop/offsetLeft are measured against the form, which
       is the panel's containing block, so this stays right wherever it mounts. */
    function place() {
      /* Measured against the FORM's padding box, not via offsetTop: offsetTop
         is relative to the nearest positioned ancestor, and in the header
         drawer the input sits inside its own positioned wrapper — which put
         the list 24px too high and 20px too far over. Rect arithmetic asks the
         one question that matters: where is the field, relative to the box
         this panel is positioned in. */
      var fr = form.getBoundingClientRect();
      /* ANCHOR TO THE FIELD AS THE CUSTOMER SEES IT. In the site-wide bar the
         white box is the FORM — the input and the submit are flex children
         inside it — so hanging the list off the input alone would start it
         after the button and make it narrower than the thing it belongs to.
         A host that marks itself data-nadi-field says "this box is the field";
         everything else (Kalles' drawer) still anchors to its input. */
      var anchor = input.closest('[data-nadi-field]') || input;
      var ar = anchor.getBoundingClientRect();
      panel.style.top = (ar.bottom - fr.top - form.clientTop + 6) + 'px';
      panel.style.left = (ar.left - fr.left - form.clientLeft) + 'px';
      panel.style.right = 'auto';
      panel.style.width = ar.width + 'px';
    }

    function show(html) {
      place();
      panel.innerHTML = html;
      rows = [].slice.call(panel.querySelectorAll('.nadi-sug__row'));
      rows.forEach(function (r, i) { r.id = id + '-' + i; });
      cursor = -1;
      panel.hidden = false;
      open = true;
      input.setAttribute('aria-expanded', 'true');
    }

    function move(step) {
      if (!open || !rows.length) return;
      if (cursor > -1) rows[cursor].setAttribute('aria-selected', 'false');
      cursor += step;
      if (cursor < -1) cursor = rows.length - 1;
      if (cursor >= rows.length) cursor = -1;
      if (cursor === -1) {
        input.removeAttribute('aria-activedescendant');
        return;
      }
      var row = rows[cursor];
      row.setAttribute('aria-selected', 'true');
      input.setAttribute('aria-activedescendant', row.id);
      /* The panel scrolls on a short phone; keep the highlighted row in view
         without scrolling the page itself. */
      if (row.scrollIntoView) row.scrollIntoView({ block: 'nearest' });
    }

    /* Something has to happen when the key is released. The answer takes about
       380ms and the wait before it took 220 more, so for half a second the
       field did exactly what it did before any of this was built: nothing.
       Three grey rows say "we heard you" and hold the space the real rows are
       about to take, so nothing jumps when they arrive. Only when there is no
       cached answer to show — an answer beats a placeholder every time. */
    var SKELETON = '<ul class="nadi-sug__list nadi-sug__list--wait" aria-hidden="true">' +
      '<li><span class="nadi-sug__sk nadi-sug__sk--img"></span><span class="nadi-sug__sk nadi-sug__sk--line"></span></li>' +
      '<li><span class="nadi-sug__sk nadi-sug__sk--img"></span><span class="nadi-sug__sk nadi-sug__sk--line"></span></li>' +
      '<li><span class="nadi-sug__sk nadi-sug__sk--img"></span><span class="nadi-sug__sk nadi-sug__sk--line"></span></li>' +
      '</ul>';

    /* The brand rows go inside the same <ul> so that arrow keys walk one list
       — two lists side by side would be two things to learn. */
    function withBrands(html, q) {
      var rows = brandRows(q);
      if (!rows) return html;
      var at = html.indexOf('>', html.indexOf('<ul'));
      if (at === -1) return rows ? '<ul class="nadi-sug__list">' + rows + '</ul>' + html : html;
      return html.slice(0, at + 1) + rows + html.slice(at + 1);
    }

    function ask(q) {
      var hit = cache.get(q);
      if (hit !== undefined) { show(withBrands(hit, q)); return; }
      if (!open) show(SKELETON);
      if (controller) controller.abort();
      controller = typeof AbortController === 'function' ? new AbortController() : null;
      var url = '/search?q=' + encodeURIComponent(q) +
        '&type=product&options%5Bprefix%5D=last&options%5Bunavailable_products%5D=last&view=' + VIEW;
      fetch(url, controller ? { signal: controller.signal } : undefined)
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (html) {
          if (!html) return;
          if (cache.size >= CACHE_MAX) cache.delete(cache.keys().next().value);
          cache.set(q, html);
          /* The field may have moved on while this was in flight. */
          if (input.value.trim() === q) show(withBrands(html, q));
        })
        .catch(function () { /* an aborted or failed lookup must never break typing */ });
    }

    input.addEventListener('input', function () {
      var q = input.value.trim();
      clearTimeout(timer);
      if (q.length < MIN) { close(); return; }
      timer = setTimeout(function () { ask(q); }, WAIT);
    });

    input.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown') { e.preventDefault(); move(1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); move(-1); }
      else if (e.key === 'Escape') { close(); }
      else if (e.key === 'Enter' && cursor > -1 && rows[cursor]) {
        /* Only when a row is highlighted: otherwise the form submits as it
           always did, and the customer lands on the full results page. */
        e.preventDefault();
        window.location.href = rows[cursor].href;
      }
    });

    input.addEventListener('focus', function () {
      if (!open && input.value.trim().length >= MIN) ask(input.value.trim());
    });

    /* Not on blur: a tap on a row blurs the field first, and closing there
       removes the row before the click lands on it. */
    document.addEventListener('click', function (e) {
      if (!form.contains(e.target)) close();
    });
    document.addEventListener('focusin', function (e) {
      if (!form.contains(e.target)) close();
    });
  }

  var FIELD = 'form[action*="/search"] input[name="q"]';

  function start() {
    /* Every search field on the page, including Kalles' own header drawer.
       That one matters more than it looks: the site-wide bar is deliberately
       absent from product pages (it cost 54px of the first screen and
       conversion with it), and product pages are where most sessions land —
       so the magnifier in the header is the ONLY way those visitors search. */
    document.querySelectorAll(FIELD).forEach(bind);
  }

  /* AND BIND AGAIN WHEN A FIELD IS FOCUSED, because the header drawer does not
     exist yet when this runs. Measured on a product page: the script binds
     nothing at load, and the drawer's input appears about a second later —
     Kalles builds that markup lazily, the same way it builds the cart drawer.
     Focus is the one moment a field is certainly there and certainly about to
     be typed into, so it costs nothing to check then. */
  document.addEventListener('focusin', function (e) {
    var t = e.target;
    if (t && t.matches && t.matches(FIELD)) bind(t);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
